import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import './UserManagement.css';
import ProfileInviteButton from '../../modules/profile/ProfileInviteButton';

interface User {
  id: number;
  name: string;
  username: string;
  role: string;
  is_active: boolean;
  created_at: string;
  invitation_pending?: boolean;
  profile_incomplete?: boolean;
}

// Local YYYY-MM-DD (avoids the UTC shift of toISOString() near midnight).
const todayISO = (): string => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { sessionToken, user: currentUser } = useAuth();
  const { showToast, fetchNotifications } = useNotification();
  const manageProfiles = currentUser?.role === 'Owner' || currentUser?.role === 'Admin';
  const [unlinked, setUnlinked] = useState<{ id: number; name: string; email: string | null; is_active: boolean }[]>([]);
  const [standalone, setStandalone] = useState(false);
  const [existingEmployeeId, setExistingEmployeeId] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    role: 'Employee'
  });

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  // Deactivation modal state — captures the employee's last working day.
  const [deactivateTarget, setDeactivateTarget] = useState<User | null>(null);
  const [lastWorkingDay, setLastWorkingDay] = useState(todayISO());

  // Inactive accounts are hidden by default; toggle reveals them so an
  // Owner/Admin can reactivate someone.
  const [search, setSearch] = useState('');
  const [inviteBusy, setInviteBusy] = useState<number | null>(null);
  const resendInvitation = async (person: User) => {
    setInviteBusy(person.id); setError('');
    try { const response = await fetch('/api/forgot-password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: person.username }) }); const data = await response.json(); if (!response.ok) throw new Error(data.error || 'Request failed'); setSuccess('Activation link requested. Ask the employee to check email and spam; delivery is not tracked here.'); } catch (e: any) { setError(e.message); } finally { setInviteBusy(null); }
  };
  const [directoryFilter, setDirectoryFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  // Load users
  useEffect(() => {
    loadUsers(showInactive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const loadUsers = async (includeInactive = showInactive) => {
    try {
      const response = await fetch(`/api/users${includeInactive ? '?includeInactive=true' : ''}`, {
        headers: {
          'X-Session-Token': sessionToken || ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      if (manageProfiles) {
        const profilesResponse = await fetch('/api/profile?directory=unlinked', { headers: { 'X-Session-Token': sessionToken || '' } });
        const profilesData = await profilesResponse.json();
        if (!profilesResponse.ok) throw new Error(profilesData.error || 'Failed to load profiles without a login');
        setUnlinked(profilesData.profiles || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (standalone) {
      try {
        const response = await fetch('/api/profile', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
          body: JSON.stringify({ standalone: true, name: formData.name }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Could not create profile');
        navigate(`/profile?employeeId=${data.employee_id}`);
      } catch (err: any) { setError(err.message); }
      return;
    }

    // Validation. No password here: the new user sets their own via the invite
    // email, so we only need their name, work email and role.
    if (!formData.name || !formData.username) {
      setError('Name, email and role are required');
      return;
    }

    // The invite is delivered to this address, so it has to be a real one.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)) {
      setError('Email must be a valid address (e.g. name@chai-social.ca)');
      return;
    }

    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken || ''
        },
        body: JSON.stringify({
          name: formData.name,
          username: formData.username,
          role: formData.role,
          ...(existingEmployeeId ? { employee_id: Number(existingEmployeeId) } : {}),
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }

      // The account exists either way; say plainly whether the email went out,
      // because if it didn't the new hire is waiting on a link that never came.
      if (data.invite_sent) {
        setSuccess(`User created. An invite to set a password was sent to ${formData.username}.`);
      } else {
        setSuccess(
          `User created, but the invite email could not be sent${data.invite_error ? ` (${data.invite_error})` : ''}. ` +
          `Ask them to use "Forgot password?" on the sign-in page.`
        );
      }
      showToast({
        type: data.invite_sent ? 'success' : 'info',
        title: data.invite_sent ? 'User Created & Invited' : 'User Created — Invite Failed',
        message: `${currentUser?.name} created new user: ${formData.name} (${formData.role})`
      });
      fetchNotifications();
      setFormData({
        name: '',
        username: '',
        role: 'Employee'
      });
      setShowAddForm(false);
      setExistingEmployeeId('');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
      showToast({
        type: 'error',
        title: 'Error',
        message: err.message || 'Failed to create user'
      });
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!passwordForm.newPassword) {
      setError('New password is required');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    // If not admin/owner and changing own password, current password is required
    const isChangingOwnPassword = selectedUser?.id === currentUser?.id;
    const isPrivileged = currentUser?.role === 'Owner' || currentUser?.role === 'Admin';

    if (isChangingOwnPassword && !isPrivileged && !passwordForm.currentPassword) {
      setError('Current password is required');
      return;
    }

    try {
      const requestBody: any = {
        userId: selectedUser?.id,
        newPassword: passwordForm.newPassword
      };

      // Include current password if provided
      if (passwordForm.currentPassword) {
        requestBody.currentPassword = passwordForm.currentPassword;
      }

      const response = await fetch('/api/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken || ''
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change password');
      }

      setSuccess(`Password changed successfully for ${selectedUser?.name}!`);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    }
  };

  const openPasswordModal = (user: User) => {
    // Check permissions
    const isPrivileged = currentUser?.role === 'Owner' || currentUser?.role === 'Admin';
    if (!isPrivileged && user.id !== currentUser?.id) {
      setError('You do not have permission to change this password');
      return;
    }

    setSelectedUser(user);
    setShowPasswordModal(true);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setError('');
    setSuccess('');
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setError('');
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'Owner':
        return 'role-badge role-owner';
      case 'Admin':
        return 'role-badge role-admin';
      case 'Manager':
        return 'role-badge role-manager';
      case 'Employee':
        return 'role-badge role-employee';
      default:
        return 'role-badge';
    }
  };

  const canEditRole = (target: User): boolean => {
    if (!currentUser) return false;
    if (target.id === currentUser.id) return false; // can't change own role
    if (currentUser.role === 'Owner') return true;
    if (currentUser.role === 'Admin') return target.role !== 'Owner'; // Admins can't touch Owners
    return false;
  };

  const canDeactivate = (target: User): boolean => {
    if (!currentUser) return false;
    if (target.id === currentUser.id) return false;
    if (currentUser.role === 'Owner') return true;
    if (currentUser.role === 'Admin') return target.role !== 'Owner';
    return false;
  };

  const openDeactivateModal = (user: User) => {
    setError('');
    setSuccess('');
    setLastWorkingDay(todayISO());
    setDeactivateTarget(user);
  };

  const closeDeactivateModal = () => {
    setDeactivateTarget(null);
    setLastWorkingDay(todayISO());
  };

  const handleConfirmDeactivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deactivateTarget) return;
    if (!lastWorkingDay) {
      setError('Last working day is required');
      return;
    }
    await runDeactivate(deactivateTarget, false, lastWorkingDay);
    closeDeactivateModal();
  };

  const handleRestore = async (user: User) => {
    if (!window.confirm(`Reactivate ${user.name}? They will be able to log in again and return to payroll.`)) return;
    await runDeactivate(user, true);
  };

  const runDeactivate = async (user: User, restore: boolean, lwd?: string) => {
    const verb = restore ? 'reactivate' : 'deactivate';
    setError('');
    setSuccess('');
    try {
      const url = `/api/users/${user.id}/deactivate${restore ? '?restore=true' : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken || '',
        },
        body: restore ? undefined : JSON.stringify({ last_working_day: lwd }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to ${verb} user`);

      const detail = !restore && data.removed_entries
        ? ` Removed from ${data.removed_entries} upcoming pay period${data.removed_entries === 1 ? '' : 's'}.`
        : '';
      setSuccess(`${user.name} ${data.action}.${detail}`);
      showToast({
        type: 'success',
        title: restore ? 'User Reactivated' : 'User Deactivated',
        message: `${user.name} has been ${data.action}.${detail}`,
      });
      loadUsers();
    } catch (err: any) {
      setError(err.message || `Failed to ${verb} user`);
      showToast({
        type: 'error',
        title: `${verb} failed`,
        message: err.message || `Failed to ${verb} user`,
      });
    }
  };

  const roleOptions = (target: User): string[] => {
    const base = ['Employee', 'Manager', 'Admin'];
    if (currentUser?.role === 'Owner') return ['Owner', ...base];
    return base;
  };

  const handleRoleChange = async (user: User, newRole: string) => {
    if (newRole === user.role) return;
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`/api/users/${user.id}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken || '',
        },
        body: JSON.stringify({ role: newRole }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to change role');

      setSuccess(`${user.name}'s role changed to ${newRole}`);
      showToast({
        type: 'success',
        title: 'Role Updated',
        message: `${user.name}: ${user.role} → ${newRole}`,
      });
      fetchNotifications();
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to change role');
      showToast({
        type: 'error',
        title: 'Role Change Failed',
        message: err.message || 'Failed to change role',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="user-management">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="header">
        <button onClick={() => navigate('/cash')} className="back-btn">← Back</button>
        <h2>User Profiles</h2>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <strong>Success:</strong> {success}
          <button onClick={() => setSuccess('')} className="alert-close">×</button>
        </div>
      )}

      {manageProfiles && <div className="actions-bar">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn-primary"
        >
          {showAddForm ? '✕ Cancel' : '+ Add User / Profile'}
        </button>
        <ProfileInviteButton all />
      </div>}

      {showAddForm && manageProfiles && (
        <div className="add-user-form">
          <h3>{standalone ? 'Create Profile Without Login' : 'Create New User'}</h3>
          <form onSubmit={handleSubmit}>
            <label><input type="checkbox" checked={standalone} onChange={(e) => { setStandalone(e.target.checked); setExistingEmployeeId(''); }} /> Profile without a login account</label>
            {!standalone && unlinked.length > 0 && <div className="form-group">
              <label htmlFor="existing-profile">Existing profile</label>
              <select id="existing-profile" value={existingEmployeeId} onChange={(e) => {
                setExistingEmployeeId(e.target.value);
                const existing = unlinked.find((profile) => profile.id === Number(e.target.value));
                if (existing) setFormData({ ...formData, name: existing.name, username: existing.email || '' });
              }}>
                <option value="">Create new profile</option>
                {unlinked.filter((profile) => profile.is_active).map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
              </select><small>Link an existing profile to retain its payroll history and documents.</small>
            </div>}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="username">Email *</label>
                <input
                  id="username"
                  disabled={standalone}
                  type="email"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().trim() })}
                  placeholder="name@chai-social.ca"
                  required
                  autoComplete="email"
                />
                <small style={{ display: 'block', color: '#666', marginTop: 4 }}>
                  Used to log in, and where the invitation is sent.
                </small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                disabled={standalone}
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              >
                <option value="Employee">Employee</option>
                <option value="Manager">Manager</option>
                <option value="Admin">Admin</option>
                {currentUser?.role === 'Owner' && <option value="Owner">Owner</option>}
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-submit">
                {standalone ? 'Create Profile' : 'Create User'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({
                    name: '',
                    username: '',
                    role: 'Employee'
                  });
                }}
                className="btn-cancel"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="cs-toolbar"><input type="search" aria-label="Search people" placeholder="Search name or email" value={search} onChange={e => setSearch(e.target.value)} /><select aria-label="People filter" value={directoryFilter} onChange={e => setDirectoryFilter(e.target.value)}><option value="all">All people</option><option value="invited">Invitation pending</option><option value="active">Active accounts</option><option value="incomplete">Missing profile details</option></select></div>
      <div className="users-list">
        <div className="users-list-header">
          <h3>{showInactive ? 'All Users' : 'Active Users'} ({users.length})</h3>
          <label className="show-inactive-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show deactivated
          </label>
        </div>
        <div className="users-grid">
          {users.filter(user => `${user.name} ${user.username}`.toLowerCase().includes(search.toLowerCase()) && (directoryFilter === 'all' || (directoryFilter === 'invited' ? user.invitation_pending : directoryFilter === 'incomplete' ? user.profile_incomplete : user.is_active && !user.invitation_pending))).map((user) => (
            <div key={user.id} className={`user-card ${!user.is_active ? 'inactive' : ''}`}>
              <div className="user-avatar">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="user-info">
                <h4>{user.name}</h4>
                <p className="username">{user.username.includes('@') ? user.username : `@${user.username}`}</p>
                <div className="user-meta">
                  <span className={getRoleBadgeClass(user.role)}>
                    {user.role}
                  </span>
                  {!user.is_active && (
                    <span className="status-badge status-inactive">Inactive</span>
                  )}
                  {user.id === currentUser?.id && (
                    <span className="status-badge status-you">You</span>
                  )}
                </div>
                {user.profile_incomplete && <p>Profile details to complete</p>}
                <p>{user.invitation_pending ? 'Invitation pending — password not set' : 'Account activated'}</p>
                <p className="created-date">
                  Created: {new Date(user.created_at).toLocaleDateString()}
                </p>
                {manageProfiles && <button className="btn-change-password" onClick={() => navigate(`/profile?userId=${user.id}`)}>Open profile</button>}
                <details><summary>Account actions</summary>{manageProfiles && user.invitation_pending && user.is_active && <button disabled={inviteBusy !== null} onClick={() => resendInvitation(user)}>{inviteBusy === user.id ? 'Requesting…' : 'Resend activation link'}</button>}
                {canEditRole(user) && (
                  <div className="role-change-row">
                    <label htmlFor={`role-${user.id}`}>Role:</label>
                    <select
                      id={`role-${user.id}`}
                      defaultValue={user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value)}
                    >
                      {roleOptions(user).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="user-card-actions">
                  {(currentUser?.role === 'Owner' || currentUser?.role === 'Admin' || user.id === currentUser?.id) && (
                    <button
                      onClick={() => openPasswordModal(user)}
                      className="btn-change-password"
                    >
                      Change Password
                    </button>
                  )}
                  {canDeactivate(user) && user.is_active && (
                    <button
                      onClick={() => openDeactivateModal(user)}
                      className="btn-deactivate"
                    >
                      Deactivate
                    </button>
                  )}
                  {canDeactivate(user) && !user.is_active && (
                    <button
                      onClick={() => handleRestore(user)}
                      className="btn-reactivate"
                    >
                      Reactivate
                    </button>
                  )}
                </div></details>
              </div>
            </div>
          ))}
        </div>
      </div>

      {manageProfiles && unlinked.length > 0 && <section className="users-list">
        <h3>Profiles without a login</h3>
        <div className="users-grid">
          {unlinked.filter((profile) => (showInactive || profile.is_active) && `${profile.name} ${profile.email || ''}`.toLowerCase().includes(search.toLowerCase())).map((profile) => <div key={profile.id} className={`user-card ${profile.is_active ? '' : 'inactive'}`}>
            <div className="user-info"><h4>{profile.name}</h4><p>{profile.email || 'No email on file'}</p><p>{profile.is_active ? 'Active' : 'Inactive'} · No login account</p>
              <button className="btn-change-password" onClick={() => navigate(`/profile?employeeId=${profile.id}`)}>Edit Profile</button>
            </div>
          </div>)}
        </div>
      </section>}

      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password for {selectedUser.name}</h3>
              <button onClick={closePasswordModal} className="modal-close">×</button>
            </div>

            {error && (
              <div className="alert alert-error">
                <strong>Error:</strong> {error}
              </div>
            )}

            <form onSubmit={handlePasswordChange}>
              {selectedUser.id === currentUser?.id && currentUser?.role !== 'Admin' && currentUser?.role !== 'Owner' && (
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password *</label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Enter your current password"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="newPassword">New Password *</label>
                <input
                  id="newPassword"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Min. 6 characters"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmNewPassword">Confirm New Password *</label>
                <input
                  id="confirmNewPassword"
                  type="password"
                  value={passwordForm.confirmNewPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmNewPassword: e.target.value })}
                  placeholder="Re-enter new password"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  Change Password
                </button>
                <button type="button" onClick={closePasswordModal} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deactivateTarget && (
        <div className="modal-overlay" onClick={closeDeactivateModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Deactivate {deactivateTarget.name}</h3>
              <button onClick={closeDeactivateModal} className="modal-close">×</button>
            </div>

            {error && (
              <div className="alert alert-error">
                <strong>Error:</strong> {error}
              </div>
            )}

            <form onSubmit={handleConfirmDeactivate}>
              <div className="form-group">
                <label htmlFor="lastWorkingDay">Last working day *</label>
                <input
                  id="lastWorkingDay"
                  type="date"
                  value={lastWorkingDay}
                  max={todayISO()}
                  onChange={(e) => setLastWorkingDay(e.target.value)}
                  required
                />
                <small style={{ display: 'block', color: '#666', marginTop: 4 }}>
                  Reported to the CRA. {deactivateTarget.name} will be logged out and
                  removed from any pay period that starts after this date. Existing
                  records (paid periods, paystubs, T4s) are kept.
                </small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit btn-deactivate-confirm">
                  Deactivate
                </button>
                <button type="button" onClick={closeDeactivateModal} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
